import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiCheck, FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminShipping = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [formData, setFormData] = useState({ name: '', delivery_fee: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('shipping_districts')
      .select('*')
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });
    if (data) setDistricts(data);
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingDistrict(null);
    setFormData({ name: '', delivery_fee: '', is_active: true });
    setIsFormOpen(true);
  };

  const handleEdit = (district) => {
    setEditingDistrict(district);
    setFormData({
      name: district.name,
      delivery_fee: district.delivery_fee,
      is_active: district.is_active
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      name: formData.name.trim(),
      delivery_fee: Number(formData.delivery_fee),
      is_active: formData.is_active,
      display_order: editingDistrict?.display_order ?? districts.length
    };

    if (editingDistrict) {
      await supabase.from('shipping_districts').update(payload).eq('id', editingDistrict.id);
    } else {
      await supabase.from('shipping_districts').insert([payload]);
    }

    setIsFormOpen(false);
    fetchDistricts();
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this district? It will no longer be available at checkout.')) {
      await supabase.from('shipping_districts').delete().eq('id', id);
      fetchDistricts();
    }
  };

  const toggleActive = async (district) => {
    await supabase
      .from('shipping_districts')
      .update({ is_active: !district.is_active })
      .eq('id', district.id);
    fetchDistricts();
  };

  const activeCount = districts.filter(d => d.is_active).length;

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-500 text-sm">Loading districts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Total Districts</p>
          <p className="text-3xl font-serif text-brand-darkSage">{districts.length}</p>
        </div>
        <div className="bg-green-50 p-5 rounded-2xl border border-green-100 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">Active (Serviced)</p>
          <p className="text-3xl font-serif text-green-700">{activeCount}</p>
        </div>
        <div className="bg-red-50 p-5 rounded-2xl border border-red-100 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-1">Inactive</p>
          <p className="text-3xl font-serif text-red-700">{districts.length - activeCount}</p>
        </div>
      </div>

      {/* Main Panel */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-serif text-gray-800">Sri Lanka Districts</h2>
            <p className="text-xs text-gray-500 mt-1">
              Manage delivery districts and their shipping fees. Only active districts appear at checkout.
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 bg-brand-darkSage text-white px-4 py-2 rounded-xl text-sm hover:bg-brand-sage transition-colors whitespace-nowrap"
          >
            <SafeIcon icon={FiPlus} /> Add District
          </button>
        </div>

        {/* District Form */}
        {isFormOpen && (
          <div className="p-6 bg-gray-50/50 border-b border-gray-100">
            <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  District Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                  placeholder="e.g. Kandy, Galle, Matara"
                />
              </div>
              <div className="w-40">
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Delivery Fee (Rs.)
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.delivery_fee}
                  onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                  placeholder="50"
                />
              </div>
              <div className="flex items-center gap-2 h-[46px]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
                <span className="text-xs font-semibold text-gray-600">Active</span>
              </div>
              <div className="flex gap-2 h-[46px]">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-brand-darkSage text-white px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingDistrict ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="bg-gray-100 text-gray-500 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* District List */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase tracking-wider">
                <th className="p-4 font-medium">District</th>
                <th className="p-4 font-medium">Delivery Fee</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {districts.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-12 text-center text-gray-400 text-sm">
                    <FiMapPin className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                    No districts added yet. Click "Add District" to get started.
                  </td>
                </tr>
              ) : districts.map((district) => (
                <tr key={district.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-sand rounded-lg flex items-center justify-center text-brand-darkSage">
                        <SafeIcon icon={FiMapPin} size={18} />
                      </div>
                      <span className="font-semibold text-gray-800">{district.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="font-mono font-bold text-brand-darkSage">
                      Rs. {Number(district.delivery_fee).toFixed(2)}
                    </span>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleActive(district)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                        district.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {district.is_active ? (
                        <><SafeIcon icon={FiCheck} size={12} /> Active</>
                      ) : (
                        <><SafeIcon icon={FiX} size={12} /> Inactive</>
                      )}
                    </button>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(district)}
                      className="p-2 text-gray-400 hover:text-brand-darkSage transition-colors"
                      title="Edit District"
                    >
                      <SafeIcon icon={FiEdit2} size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(district.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete District"
                    >
                      <SafeIcon icon={FiTrash2} size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminShipping;